import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { Activity, Hourglass, CheckCircle2, HardDrive, Rocket, ArrowRight, Boxes } from "lucide-react";

const Widget = ({ icon: Icon, label, value, sub, testid, accent = "#00D4FF" }) => (
  <div className="holo-panel p-6 relative overflow-hidden" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="label-overline">{label}</div>
        <div className="font-display font-black text-3xl mt-3 tracking-tighter">{value}</div>
        {sub && <div className="font-mono-data text-[11px] text-white/55 mt-2">{sub}</div>}
      </div>
      <div className="w-10 h-10 grid place-items-center border border-[color:var(--accent)]" style={{ borderColor: accent + "55" }}>
        <Icon size={16} style={{ color: accent }} />
      </div>
    </div>
  </div>
);

const fmtBytes = (b) => {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 ? 0 : 1)} ${u[i]}`;
};

export default function Dashboard() {
  const { t } = useLang();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const d = data || { active_missions: 0, pending_transmissions: 0, completed_missions: 0, storage_used: 0, storage_cap: 5 * 1024 * 1024 * 1024, history: [], capsules_total: 0 };
  const pct = Math.min(100, (d.storage_used / d.storage_cap) * 100);

  return (
    <AppShell>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-overline">Operator Console</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-2" data-testid="dashboard-title">{t("dashboard.title")}</h1>
        </div>
        <button
          onClick={() => nav("/upload")}
          className="btn-primary-s2s"
          data-testid="dashboard-new-transmission"
        >
          <Rocket size={14} /> {t("start")}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Widget icon={Activity} label={t("dashboard.active")} value={d.active_missions} testid="widget-active" />
        <Widget icon={Hourglass} label={t("dashboard.pending")} value={d.pending_transmissions} testid="widget-pending" accent="#FFB800" />
        <Widget icon={CheckCircle2} label={t("dashboard.completed")} value={d.completed_missions} testid="widget-completed" accent="#00FFAA" />
        <Widget icon={Boxes} label={t("dashboard.capsules")} value={d.capsules_total} testid="widget-capsules" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="holo-panel p-6 lg:col-span-2" data-testid="storage-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-overline">{t("dashboard.storage")}</div>
              <div className="font-display font-black text-2xl tracking-tighter mt-2">
                {fmtBytes(d.storage_used)} <span className="text-white/40 text-base font-normal">/ {fmtBytes(d.storage_cap)}</span>
              </div>
            </div>
            <HardDrive size={18} className="text-[#00D4FF]" />
          </div>
          <div className="h-2 w-full bg-white/5 relative overflow-hidden">
            <div className="absolute left-0 top-0 h-full bg-[#00D4FF]" style={{ width: `${pct}%` }} />
          </div>
          <div className="font-mono-data text-[11px] tracking-[0.18em] uppercase text-white/55 mt-3">{pct.toFixed(1)}% utilized</div>
        </div>

        <div className="holo-panel p-6" data-testid="history-panel">
          <div className="label-overline mb-4">{t("dashboard.history")}</div>
          {d.history.length === 0 ? (
            <div className="text-white/55 text-sm">{t("dashboard.no_history")}</div>
          ) : (
            <div className="space-y-3 max-h-72 overflow-auto pr-1">
              {d.history.map((h) => (
                <button
                  key={h.transmission_id}
                  onClick={() => nav(`/transmission/${h.transmission_id}`)}
                  data-testid={`history-${h.transmission_id}`}
                  className="block w-full text-left border border-white/5 hover:border-[#00D4FF]/40 p-3 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-mono-data text-[11px] text-[#00D4FF]">{h.transmission_id}</div>
                    <ArrowRight size={12} className="text-white/40" />
                  </div>
                  <div className="text-sm mt-1 truncate">{h.capsule_name}</div>
                  <div className="font-mono-data text-[10px] text-white/45 mt-1 uppercase">{h.status.replace(/_/g, " ")}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
